import { useEffect, useRef, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  Clock,
  FileText,
  Info,
  Mic,
  Pause,
  Play,
  Send,
  Sparkles,
  Square,
  Target,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { AiCreditUsageLink } from '@/components/shared/AiCreditUsageLink'
import {
  analyseDealVendor,
  createDealNote,
  createDealVoiceNote,
  getDealVendor,
  updateDealVendor,
} from '@/lib/api'
import { looksPersistedDealId } from '@/lib/persisted-deal'
import { useSession } from '@/lib/session-context'
import { cn } from '@/lib/utils'

const INITIAL_ENTRIES = [
  {
    id: 1,
    type: 'AI Insight',
    date: 'Today, 2:45 PM',
    content: 'Contradiction detected: Vendor claims no rush, but agent mentioned they need to relocate before September term starts.',
  },
  {
    id: 3,
    type: 'Manual Note',
    date: 'Mon, 13 Apr, 09:15 AM',
    content: 'Initial viewing completed. Property is vacant but heating was left on. Previous buyer pulled out on survey - need to investigate the roof.',
  },
]

const INTELLIGENCE = {
  motivation: 'High',
  timePressure: 'Strong',
  angleLabel: 'Speed-led negotiation',
  angle: 'Use speed and certainty to protect price.',
  riskFlags: 'Divorce Sale - Emotional volatility',
}

const BLANK_PROFILE = {
  motivationLevel: null,
  motivationLabel: null,
  motivationSummary: null,
  timePressureLevel: null,
  timePressureLabel: null,
  timePressureSummary: null,
  negotiationAngleLabel: null,
  negotiationAngle: null,
  entityType: null,
  ownershipDuration: null,
  agentRelationship: null,
  knownSecuredDebt: null,
  riskFlags: [],
  intelligenceStatus: 'not_analysed',
  intelligenceError: null,
  intelligenceConfidence: null,
}

const PENDING_INTELLIGENCE_STATUSES = new Set(['queued', 'running'])

const PROMPTS = [
  'Why are they selling?',
  'What is their absolute deadline?',
  'Any previous offers fallen through?',
]

const FILTERS = ['All', 'Voice', 'Note', 'AI']

const FILTER_ENTRY_TYPES = {
  Voice: 'Voice Note',
  Note: 'Manual Note',
  AI: 'AI Insight',
}

const AUDIO_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
]

const TRANSCRIPTION_UNSUPPORTED_MESSAGE = 'Transcription is not supported in this browser.'

const ENTRY_STYLES = {
  'AI Insight': {
    icon: Sparkles,
    tone: 'primary',
  },
  'Voice Note': {
    icon: Mic,
    tone: 'foreground',
  },
  'Manual Note': {
    icon: FileText,
    tone: 'neutral',
  },
  'Vendor Intelligence': {
    icon: Target,
    tone: 'warning',
  },
}

const TONE_CLASSES = {
  primary: 'border-primary/20 bg-primary/10 text-primary',
  warning: 'border-chart-4/20 bg-chart-4/10 text-chart-4',
  destructive: 'border-destructive/20 bg-destructive/10 text-destructive',
  risk: 'border-destructive/20 bg-destructive/10 text-destructive',
  foreground: 'border-foreground bg-foreground text-background',
  neutral: 'border-border bg-muted text-muted-foreground',
}

function normalizeProfile(profile) {
  return { ...BLANK_PROFILE, ...(profile ?? {}) }
}

function displayProfileValue(value, fallback = 'Unknown') {
  return value?.toString().trim() || fallback
}

function fallbackNegotiationAngleLabel(value) {
  const angle = displayProfileValue(value, 'Not captured')
  if (angle === 'Not captured') return angle

  const normalizedAngle = angle.toLowerCase()
  if (/lease option|vendor finance|creative/.test(normalizedAngle)) return 'Creative structure'
  if (/certainty|reliable completion/.test(normalizedAngle)) return 'Certainty-led offer'
  if (/speed|deadline|fast/.test(normalizedAngle)) return 'Speed-led negotiation'
  if (/evidence|verify/.test(normalizedAngle)) return 'Evidence-led offer'
  if (/discount|price/.test(normalizedAngle)) return 'Discount-led offer'

  return 'Review negotiation angle'
}

function compactNegotiationAngleLabel(label, detail) {
  const normalizedLabel = displayProfileValue(label, '')
  const wordCount = normalizedLabel.split(/\s+/).filter(Boolean).length

  if (wordCount > 0 && wordCount <= 4) return normalizedLabel

  return fallbackNegotiationAngleLabel(detail)
}

function currencyInputValue(value) {
  return value === null || value === undefined ? '' : String(value)
}

function normalizeCurrencyInput(value) {
  return value.replace(/\D/g, '')
}

function summaryFromProfile(profile) {
  const normalizedProfile = normalizeProfile(profile)
  const riskFlags = normalizedProfile.riskFlags ?? []
  const angleDetail = displayProfileValue(normalizedProfile.negotiationAngle, 'Not captured')

  return {
    motivation: displayProfileValue(
      normalizedProfile.motivationLabel || normalizedProfile.motivationSummary,
    ),
    timePressure: displayProfileValue(
      normalizedProfile.timePressureLabel || normalizedProfile.timePressureSummary,
    ),
    angleLabel: compactNegotiationAngleLabel(
      normalizedProfile.negotiationAngleLabel,
      angleDetail,
    ),
    angleDetail,
    riskFlags,
    riskSummary: riskFlags.length === 0
      ? 'None detected'
      : `${riskFlags.length} ${riskFlags.length === 1 ? 'signal' : 'signals'} detected`,
  }
}

function intelligenceStatusLabel(status) {
  if (status === 'queued') return 'Queued'
  if (status === 'running') return 'Analysing'
  if (status === 'current') return 'Current'
  if (status === 'failed') return 'Needs retry'
  return 'Not analysed'
}

function noteEntry(note) {
  const content = note.content || note.body
  const placeholderTranscript = note.audioAttached && content === 'Voice note recorded.'

  return {
    id: note.id,
    type: note.type || note.categoryLabel || 'Manual Note',
    date: note.date || 'Just now',
    duration: note.duration,
    content: placeholderTranscript ? '' : content,
    audioUrl: note.audioUrl,
    audioAttached: note.audioAttached,
  }
}

function preferredAudioMimeType() {
  if (!window.MediaRecorder?.isTypeSupported) return undefined

  return AUDIO_MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported(type))
}

function formatDuration(milliseconds) {
  const totalSeconds = Math.max(1, Math.round(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function formatPlaybackTime(seconds) {
  if (!Number.isFinite(seconds)) return '0:00'

  const totalSeconds = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(totalSeconds / 60)
  const remainingSeconds = totalSeconds % 60

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

function buildVoiceFile(blob) {
  return new File([blob], `vendor-voice-note-${Date.now()}.webm`, {
    type: blob.type || 'audio/webm',
  })
}

function speechRecognitionConstructor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition
}

function transcriptFromResult(event) {
  let finalTranscript = ''
  let interimTranscript = ''

  for (let index = event.resultIndex; index < event.results.length; index += 1) {
    const result = event.results[index]
    const transcript = result[0]?.transcript?.trim()
    if (!transcript) continue

    if (result.isFinal) {
      finalTranscript = `${finalTranscript} ${transcript}`.trim()
    } else {
      interimTranscript = `${interimTranscript} ${transcript}`.trim()
    }
  }

  return { finalTranscript, interimTranscript }
}

export function DealVendorTab({ deal, onComplete }) {
  const { currentUser, csrfToken } = useSession()
  const shouldPersist = Boolean(currentUser && looksPersistedDealId(deal.id))
  const [filter, setFilter] = useState('All')
  const [inputText, setInputText] = useState('')
  const [profile, setProfile] = useState(() => (shouldPersist ? BLANK_PROFILE : normalizeProfile({
    motivationLabel: INTELLIGENCE.motivation,
    timePressureLabel: INTELLIGENCE.timePressure,
    negotiationAngleLabel: INTELLIGENCE.angleLabel,
    negotiationAngle: INTELLIGENCE.angle,
    riskFlags: [INTELLIGENCE.riskFlags],
    entityType: 'Private Individual',
    ownershipDuration: '12 Years (Since 2014)',
    agentRelationship: 'Sole Agency (Expiring soon)',
  })))
  const [isRecording, setIsRecording] = useState(false)
  const [isSavingVoice, setIsSavingVoice] = useState(false)
  const [transcriptPreview, setTranscriptPreview] = useState('')
  const [transcriptionStatus, setTranscriptionStatus] = useState(null)
  const [entries, setEntries] = useState(() => (shouldPersist ? [] : INITIAL_ENTRIES))
  const [securedDebtInput, setSecuredDebtInput] = useState('')
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isRequestingAnalysis, setIsRequestingAnalysis] = useState(false)
  const [error, setError] = useState(null)
  const recorderRef = useRef(null)
  const recognitionRef = useRef(null)
  const streamRef = useRef(null)
  const audioChunksRef = useRef([])
  const recordingStartedAtRef = useRef(null)
  const finalTranscriptRef = useRef('')
  const interimTranscriptRef = useRef('')
  const localAudioUrlsRef = useRef([])
  const summary = summaryFromProfile(profile)

  useEffect(() => {
    if (!shouldPersist) return undefined

    let ignore = false

    getDealVendor(deal.id, { csrfToken })
      .then((payload) => {
        if (ignore) return

        const nextProfile = normalizeProfile(payload.profile)
        setProfile(nextProfile)
        setSecuredDebtInput(currencyInputValue(nextProfile.knownSecuredDebt))
        setEntries((payload.notes ?? []).map(noteEntry))
      })
      .catch((loadError) => {
        if (!ignore) setError(loadError.message)
      })

    return () => { ignore = true }
  }, [csrfToken, deal.id, shouldPersist])

  useEffect(() => {
    if (!shouldPersist || !PENDING_INTELLIGENCE_STATUSES.has(profile.intelligenceStatus)) {
      return undefined
    }

    let ignore = false
    const intervalId = window.setInterval(() => {
      getDealVendor(deal.id, { csrfToken })
        .then((payload) => {
          if (ignore) return

          const nextProfile = normalizeProfile(payload.profile)
          setProfile(nextProfile)
          setEntries((payload.notes ?? []).map(noteEntry))
        })
        .catch((loadError) => {
          if (!ignore) setError(loadError.message)
        })
    }, 1500)

    return () => {
      ignore = true
      window.clearInterval(intervalId)
    }
  }, [csrfToken, deal.id, profile.intelligenceStatus, shouldPersist])

  useEffect(() => () => {
    const recorder = recorderRef.current
    if (recorder) {
      recorder.ondataavailable = null
      recorder.onstop = null
      if (recorder.state === 'recording') recorder.stop()
    }
    recognitionRef.current?.abort?.()
    streamRef.current?.getTracks().forEach((track) => track.stop())
    localAudioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  const filteredEntries = filter === 'All'
    ? entries
    : entries.filter((entry) => entry.type === FILTER_ENTRY_TYPES[filter])

  const handleSaveNote = async () => {
    if (!inputText.trim()) return

    if (shouldPersist) {
      try {
        setError(null)
        const payload = await createDealNote(deal.id, {
          category: 'note',
          body: inputText.trim(),
          source: 'manual',
          activitySource: 'manual',
          metadata: { context: 'vendor' },
        }, { csrfToken })
        setEntries((currentEntries) => [noteEntry(payload.note), ...currentEntries])
        setProfile((currentProfile) => ({ ...currentProfile, intelligenceStatus: 'queued' }))
        setInputText('')
        if (payload.complete) onComplete?.()
      } catch (saveError) {
        setError(saveError.message)
      }
      return
    }

    setEntries((currentEntries) => [
      { id: Date.now(), type: 'Manual Note', date: 'Just now', content: inputText.trim() },
      ...currentEntries,
    ])
    setInputText('')
    onComplete?.()
  }

  const handleSaveProfile = async () => {
    const knownSecuredDebt = securedDebtInput.trim()

    if (shouldPersist) {
      try {
        setError(null)
        setIsSavingProfile(true)
        const payload = await updateDealVendor(deal.id, {
          knownSecuredDebt,
          activitySource: 'manual',
        }, { csrfToken })
        const nextProfile = normalizeProfile(payload.profile)
        setProfile(nextProfile)
        setSecuredDebtInput(currencyInputValue(nextProfile.knownSecuredDebt))
      } catch (saveError) {
        setError(saveError.message)
      } finally {
        setIsSavingProfile(false)
      }
      return
    }

    setProfile((currentProfile) => ({
      ...currentProfile,
      knownSecuredDebt: knownSecuredDebt || null,
    }))
  }

  const saveVoiceBlob = async (blob, duration, transcript) => {
    const voiceContent = transcript || 'Voice note recorded.'

    if (shouldPersist) {
      try {
        setError(null)
        setIsSavingVoice(true)
        const payload = await createDealVoiceNote(deal.id, {
          category: 'voice_note',
          source: 'manual',
          body: voiceContent,
          metadata: { duration, context: 'vendor' },
          activitySource: 'manual',
          audio: buildVoiceFile(blob),
        }, { csrfToken })
        setEntries((currentEntries) => [noteEntry(payload.note), ...currentEntries])
        setProfile((currentProfile) => ({ ...currentProfile, intelligenceStatus: 'queued' }))
        if (payload.complete) onComplete?.()
      } catch (saveError) {
        setError(saveError.message)
      } finally {
        setIsRecording(false)
        setIsSavingVoice(false)
      }
      return
    }

    const audioUrl = URL.createObjectURL(blob)
    localAudioUrlsRef.current.push(audioUrl)
    setEntries((currentEntries) => [
      {
        id: Date.now(),
        type: 'Voice Note',
        date: 'Just now',
        duration,
        content: transcript,
        audioUrl,
        audioAttached: true,
      },
      ...currentEntries,
    ])
    setIsRecording(false)
    onComplete?.()
  }

  const startTranscription = () => {
    const SpeechRecognition = speechRecognitionConstructor()
    if (!SpeechRecognition) {
      setTranscriptionStatus(TRANSCRIPTION_UNSUPPORTED_MESSAGE)
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = navigator.language || 'en-GB'
    recognition.onresult = (event) => {
      const { finalTranscript, interimTranscript: nextInterimTranscript } = transcriptFromResult(event)

      if (finalTranscript) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscript}`.trim()
      }
      interimTranscriptRef.current = nextInterimTranscript
      setTranscriptPreview(currentTranscript())
      setTranscriptionStatus(null)
    }
    recognition.onerror = () => {
      setTranscriptionStatus('Transcription paused. Audio will still be saved.')
    }

    try {
      recognition.start()
      recognitionRef.current = recognition
      setTranscriptionStatus(null)
    } catch {
      setTranscriptionStatus('Transcription could not start. Audio will still be saved.')
    }
  }

  const stopTranscription = () => {
    recognitionRef.current?.stop?.()
    recognitionRef.current = null
  }

  const currentTranscript = () => (
    `${finalTranscriptRef.current} ${interimTranscriptRef.current}`.trim()
  )

  const handleStartRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError('Voice recording is not supported in this browser')
      return
    }

    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = preferredAudioMimeType()
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined)

      streamRef.current = stream
      recorderRef.current = recorder
      audioChunksRef.current = []
      recordingStartedAtRef.current = window.performance.now()
      finalTranscriptRef.current = ''
      interimTranscriptRef.current = ''
      setTranscriptPreview('')
      setTranscriptionStatus(null)

      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) audioChunksRef.current.push(event.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' })
        const duration = formatDuration(window.performance.now() - recordingStartedAtRef.current)

        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        recorderRef.current = null

        if (blob.size === 0) {
          setError('No audio was captured')
          setIsRecording(false)
          return
        }

        stopTranscription()
        void saveVoiceBlob(blob, duration, currentTranscript())
      }

      recorder.start()
      startTranscription()
      setIsRecording(true)
    } catch (recordingError) {
      setError(recordingError.message || 'Could not start voice recording')
    }
  }

  const handleStopRecording = () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  const handleAnalyse = async () => {
    if (!shouldPersist) return

    try {
      setError(null)
      setIsRequestingAnalysis(true)
      const payload = await analyseDealVendor(deal.id, { csrfToken })
      setProfile(normalizeProfile(payload.profile))
      setEntries((payload.notes ?? []).map(noteEntry))
    } catch (analysisError) {
      setError(analysisError.message)
    } finally {
      setIsRequestingAnalysis(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] pb-24">
      {error ? (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive">
          {error} <AiCreditUsageLink error={error} />
        </div>
      ) : null}
      <VendorSummary
        summary={summary}
        status={profile.intelligenceStatus}
        error={profile.intelligenceError}
        canAnalyse={shouldPersist}
        isRequesting={isRequestingAnalysis}
        onAnalyse={handleAnalyse}
      />
      <div className="flex flex-col gap-10 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-8">
          <VendorCapture
            inputText={inputText}
            isRecording={isRecording}
            isSavingVoice={isSavingVoice}
            transcriptionText={transcriptPreview}
            transcriptionStatus={transcriptionStatus}
            onInputTextChange={setInputText}
            onPromptClick={(prompt) => setInputText(`${prompt} `)}
            onStartRecording={handleStartRecording}
            onSaveVoice={handleStopRecording}
            onSaveNote={handleSaveNote}
          />
          <VendorFeed
            filter={filter}
            entries={filteredEntries}
            onFilterChange={setFilter}
          />
        </div>
        <VendorProfile
          profile={profile}
          securedDebtInput={securedDebtInput}
          isSaving={isSavingProfile}
          onSecuredDebtChange={(value) => setSecuredDebtInput(normalizeCurrencyInput(value))}
          onSave={handleSaveProfile}
        />
      </div>
    </div>
  )
}

function VendorSummary({ summary, status, error, canAnalyse, isRequesting, onAnalyse }) {
  const [expandedDetail, setExpandedDetail] = useState(null)
  const summaryItems = [
    {
      label: 'Motivation',
      value: summary.motivation,
      icon: Activity,
      tone: 'destructive',
    },
    {
      label: 'Time Pressure',
      value: summary.timePressure,
      icon: Clock,
      tone: 'warning',
    },
  ]
  const hasRisks = summary.riskFlags.length > 0
  const hasAngleDetail = summary.angleDetail !== 'Not captured'

  return (
    <section className="mb-8 overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-border/50 bg-muted/30 px-5 py-3">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>
            AI Intelligence Summary
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <span className="rounded-md border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-primary">
            {intelligenceStatusLabel(status)}
          </span>
          {canAnalyse && !PENDING_INTELLIGENCE_STATUSES.has(status) ? (
            <Button type="button" size="xs" variant="outline" onClick={onAnalyse} disabled={isRequesting}>
              <Sparkles />
              {status === 'failed' ? 'Retry' : 'Analyse'}
            </Button>
          ) : null}
        </div>
      </div>

      {status === 'failed' && error ? (
        <p className="border-b border-destructive/20 bg-destructive/10 px-5 py-2 text-xs font-medium text-destructive">
          Analysis failed. Retry when ready. <AiCreditUsageLink error={error} />
        </p>
      ) : null}

      <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
        {summaryItems.map((item) => {
          const Icon = item.icon

          return (
            <article key={item.label} className="space-y-1 p-5">
              <span className="block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {item.label}
              </span>
              <div className="flex items-center gap-2">
                {item.tone === 'destructive' ? (
                  <span className="h-2 w-2 rounded-full bg-destructive shadow-sm" />
                ) : (
                  <Icon
                    className={cn(
                      'h-3.5 w-3.5',
                      item.tone === 'warning' && 'text-chart-4',
                      item.tone === 'primary' && 'text-primary',
                      item.tone === 'risk' && 'text-destructive',
                    )}
                  />
                )}
                <p
                  className={cn(
                    'text-sm font-bold text-foreground',
                    item.tone === 'risk' && 'text-destructive',
                  )}
                >
                  {item.value}
                </p>
              </div>
            </article>
          )
        })}

        <button
          type="button"
          className={cn(
            'min-h-24 space-y-1 p-5 text-left transition-colors',
            hasAngleDetail && 'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          )}
          aria-label="Negotiation angle"
          aria-expanded={expandedDetail === 'angle'}
          aria-controls="vendor-negotiation-angle"
          disabled={!hasAngleDetail}
          onClick={() => setExpandedDetail((current) => (current === 'angle' ? null : 'angle'))}
        >
          <span className="block text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Negotiation Angle
          </span>
          <span className="flex items-center gap-2 text-sm font-bold text-foreground">
            <Target className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="line-clamp-2">{summary.angleLabel}</span>
            {hasAngleDetail ? (
              <ChevronDown
                className={cn(
                  'ml-auto h-4 w-4 shrink-0 transition-transform',
                  expandedDetail === 'angle' && 'rotate-180',
                )}
              />
            ) : null}
          </span>
        </button>

        <button
          type="button"
          className={cn(
            'min-h-24 space-y-1 p-5 text-left transition-colors',
            hasRisks && 'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          )}
          aria-label="Vendor risks"
          aria-expanded={expandedDetail === 'risks'}
          aria-controls="vendor-risk-signals"
          disabled={!hasRisks}
          onClick={() => setExpandedDetail((current) => (current === 'risks' ? null : 'risks'))}
        >
          <span className="block text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Vendor Risks
          </span>
          <span className="flex items-center gap-2 text-sm font-bold text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>{summary.riskSummary}</span>
            {hasRisks ? (
              <ChevronDown
                className={cn(
                  'ml-auto h-4 w-4 shrink-0 transition-transform',
                  expandedDetail === 'risks' && 'rotate-180',
                )}
              />
            ) : null}
          </span>
        </button>
      </div>

      {expandedDetail === 'angle' && hasAngleDetail ? (
        <div id="vendor-negotiation-angle" className="border-t border-border bg-muted/20 px-5 py-4">
          <h3 className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Negotiation Angle
          </h3>
          <p className="text-sm leading-6 text-foreground">{summary.angleDetail}</p>
        </div>
      ) : null}

      {expandedDetail === 'risks' && hasRisks ? (
        <div id="vendor-risk-signals" className="border-t border-border bg-muted/20 px-5 py-4">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Vendor Risk Signals
          </h3>
          <ul className="divide-y divide-border">
            {summary.riskFlags.map((risk, index) => (
              <li
                key={`${risk}-${index}`}
                className="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span className="text-sm leading-6 text-foreground">{risk}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

function VendorCapture({
  inputText,
  isRecording,
  isSavingVoice,
  transcriptionText,
  transcriptionStatus,
  onInputTextChange,
  onPromptClick,
  onStartRecording,
  onSaveVoice,
  onSaveNote,
}) {
  const handleCaptureKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return

    event.preventDefault()
    onSaveNote()
  }

  return (
    <section className="relative">
      <div className="rounded-lg border border-border bg-card p-2 text-card-foreground shadow-sm transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        {isRecording ? (
          <>
            <div className="flex items-center gap-4 rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3">
              <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/20">
                <span className="absolute h-3 w-3 animate-ping rounded-full bg-destructive" />
                <span className="relative h-3 w-3 rounded-full bg-destructive" />
              </div>
              <div
                role="status"
                aria-live="polite"
                className="min-w-0 flex-1"
              >
                <span className="block truncate text-sm font-bold text-destructive">
                  {isSavingVoice ? 'Saving Voice Note...' : 'Recording Voice Note...'}
                </span>
                <span className="block text-xs font-medium uppercase tracking-widest text-destructive/80">
                  {isSavingVoice ? 'Uploading audio' : 'Listening to context'}
                </span>
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={onSaveVoice}
                disabled={isSavingVoice}
              >
                <Square />
                Stop & Save
              </Button>
            </div>
            {transcriptionText || transcriptionStatus ? (
              <div className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground">
                {transcriptionText || transcriptionStatus}
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <Textarea
              id="vendor-intelligence"
              aria-label="Capture intelligence"
              value={inputText}
              onChange={(event) => onInputTextChange(event.target.value)}
              onKeyDown={handleCaptureKeyDown}
              placeholder="Capture intelligence (or record a voice note)..."
              rows={1}
              className="max-h-32 min-h-[52px] flex-1 resize-none border-0 bg-transparent px-3 py-3 text-sm font-medium shadow-none focus-visible:border-transparent focus-visible:ring-0"
            />
            <div className="flex shrink-0 items-center gap-2 p-1">
              <Button
                type="button"
                variant="ghost"
                onClick={onStartRecording}
                className="bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
              >
                <Mic />
                Record
              </Button>
              <Button
                type="button"
                aria-label="Save vendor note"
                size="icon-sm"
                onClick={onSaveNote}
                disabled={!inputText.trim()}
              >
                <Send />
              </Button>
            </div>
          </div>
        )}
      </div>

      {!isRecording && !inputText ? (
        <div className="flex gap-2 overflow-x-auto pt-3">
          {PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              type="button"
              variant="outline"
              size="xs"
              onClick={() => onPromptClick(prompt)}
              className="shrink-0 border-border/50 bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {prompt}
            </Button>
          ))}
        </div>
      ) : null}
    </section>
  )
}

function VendorFeed({ filter, entries, onFilterChange }) {
  return (
    <section>
      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border/50 bg-background/80 py-3 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground">
          <Activity className="h-4 w-4 text-muted-foreground" />
          Intelligence Feed
        </h3>
        <div className="flex w-fit rounded-lg border border-border/50 bg-muted/50 p-1">
          {FILTERS.map((option) => (
            <Button
              key={option}
              type="button"
              variant="ghost"
              size="xs"
              aria-pressed={filter === option}
              onClick={() => onFilterChange(option)}
              className={cn(
                'rounded-md px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground',
                filter === option && 'bg-background text-foreground shadow-sm',
              )}
            >
              {option}
            </Button>
          ))}
        </div>
      </div>

      <div className="relative space-y-5 pt-5 before:absolute before:inset-y-5 before:left-6 before:w-px before:bg-border/60">
        {entries.map((entry) => (
          <VendorFeedEntry key={entry.id} entry={entry} />
        ))}

        {entries.length === 0 ? (
          <div className="ml-16 rounded-lg border-2 border-dashed border-border py-12 text-center">
            <Info className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium text-muted-foreground">
              No intelligence captured yet for this filter.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function VendorFeedEntry({ entry }) {
  const entryStyle = ENTRY_STYLES[entry.type] ?? ENTRY_STYLES['Manual Note']
  const Icon = entryStyle.icon

  return (
    <div className="group relative flex gap-5">
      <div className="z-10 mt-1 flex w-12 shrink-0 justify-center">
        <span
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-full border-4 border-background shadow-sm',
            TONE_CLASSES[entryStyle.tone],
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>

      <article
        className={cn(
          'flex-1 rounded-lg border border-border bg-card p-4 text-card-foreground shadow-sm transition-shadow group-hover:shadow-md',
          entry.type === 'AI Insight' && 'border-primary/20 bg-primary/5 text-primary',
        )}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'rounded-sm px-2 py-0.5 text-xs font-bold uppercase tracking-widest',
                entry.type === 'AI Insight' && 'bg-primary/10 text-primary',
                entry.type === 'Voice Note' && 'bg-muted text-foreground',
                entry.type === 'Manual Note' && 'bg-muted/50 text-muted-foreground',
                entry.type === 'Vendor Intelligence' && 'bg-chart-4/10 text-chart-4',
              )}
            >
              {entry.type}
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {entry.date}
            </span>
          </div>
        </div>

        {entry.type === 'Voice Note' ? (
          <>
            <VoiceNotePlayer entry={entry} />
            {entry.content ? (
              <p className="border-l-2 border-muted-foreground/30 pl-3 text-sm font-medium italic leading-6 text-muted-foreground">
                &quot;{entry.content}&quot;
              </p>
            ) : (
              <p className="border-l-2 border-muted-foreground/30 pl-3 text-sm font-medium italic leading-6 text-muted-foreground">
                No transcript yet.
              </p>
            )}
          </>
        ) : (
          <p
            className={cn(
              'text-sm leading-6 text-foreground',
              entry.type === 'AI Insight' && 'font-medium text-primary',
            )}
          >
            {entry.content}
          </p>
        )}
      </article>
    </div>
  )
}

function VoiceNotePlayer({ entry }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0
  const displayedDuration = entry.duration || formatPlaybackTime(duration)

  const handleTogglePlayback = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      try {
        await audio.play()
      } catch {
        setIsPlaying(false)
      }
      return
    }

    audio.pause()
  }

  return (
    <div className="mb-3 flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 p-2">
      {entry.audioUrl ? (
        <audio
          ref={audioRef}
          aria-label="Voice note audio"
          src={entry.audioUrl}
          preload="metadata"
          className="hidden"
          onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
          onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false)
            setCurrentTime(0)
          }}
        >
          <track kind="captions" />
        </audio>
      ) : null}
      <Button
        type="button"
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
        variant="ghost"
        size="icon-sm"
        disabled={!entry.audioUrl}
        onClick={handleTogglePlayback}
        className="size-8 shrink-0 rounded-full bg-foreground text-background hover:scale-105 hover:bg-foreground hover:text-background"
      >
        {isPlaying ? (
          <Pause className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Play className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </Button>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted-foreground/20">
        <div
          className="h-full origin-left rounded-full bg-foreground"
          style={{ transform: `scaleX(${entry.audioUrl ? progress : 0.33})` }}
        />
      </div>
      <span className="text-xs font-bold text-muted-foreground">
        {displayedDuration}
      </span>
    </div>
  )
}

function VendorProfile({
  profile,
  securedDebtInput,
  isSaving,
  onSecuredDebtChange,
  onSave,
}) {
  const isDirty = securedDebtInput !== currencyInputValue(profile.knownSecuredDebt)

  return (
    <aside className="w-full rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm lg:sticky lg:top-8 lg:w-[280px] lg:shrink-0 lg:self-start">
      <h3 className="mb-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        Vendor Profile
      </h3>

      <div className="space-y-4">
        <ProfileRow label="Entity Type" value={displayProfileValue(profile.entityType)} />
        <hr className="border-border/50" />
        <ProfileRow label="Ownership Duration" value={displayProfileValue(profile.ownershipDuration)} />
        <hr className="border-border/50" />
        <ProfileRow label="Agent Relation" value={displayProfileValue(profile.agentRelationship)} />
        <hr className="border-border/50" />
        <div className="space-y-2">
          <label
            htmlFor="known-secured-debt"
            className="block text-xs font-bold uppercase tracking-widest text-muted-foreground"
          >
            Known mortgage / secured debt
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-semibold text-muted-foreground">
              £
            </span>
            <Input
              id="known-secured-debt"
              autoComplete="off"
              type="text"
              inputMode="numeric"
              value={securedDebtInput}
              onChange={(event) => onSecuredDebtChange(event.target.value)}
              className="pl-7"
            />
          </div>
          <p className="text-xs leading-5 text-muted-foreground">
            Optional known balance.
          </p>
          <Button
            type="button"
            size="sm"
            disabled={!isDirty || isSaving}
            onClick={onSave}
            className="w-full"
          >
            {isSaving ? 'Saving...' : 'Save debt'}
          </Button>
        </div>
      </div>
    </aside>
  )
}

function ProfileRow({ label, value }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  )
}
